import { motion } from 'framer-motion';
import { Heart, Utensils, TrendingUp, Users, Shield, Sparkles, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Utensils,
      title: 'Planuri de Mese Personalizate',
      description: 'Generează automat planuri de mese adaptate nevoilor tale cu ajutorul AI',
    },
    {
      icon: TrendingUp,
      title: 'Monitorizare Sănătate',
      description: 'Urmărește glicemia, colesterolul și tensiunea cu praguri OMS',
    },
    {
      icon: Sparkles,
      title: 'Recomandări AI',
      description: 'Primęste sfaturi personalizate bazate pe datele tale de sănătate',
    },
    {
      icon: Users,
      title: 'Componăntă Socială',
      description: 'Donații alimentare și acces pentru utilizatori vulnerabili',
    },
    {
      icon: Shield,
      title: 'Sigur și Conform GDPR',
      description: 'Datele tale sunt protejate cu autentificare 2FA',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Navigation */}
      <nav className="glass-morphism fixed top-0 w-full z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#1A4D2E] rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-serif font-bold text-[#1A4D2E]">NutriHealth</span>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/recipes"
                className="hidden md:flex items-center gap-1 text-[#1A4D2E] hover:text-[#143d24] font-medium transition-colors"
              >
                <ChefHat className="w-4 h-4" />
                Rețete
              </Link>
              <Button
                onClick={() => navigate('/auth')}
                data-testid="get-started-btn"
                className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Începe Acum
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-4 md:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#1A4D2E] mb-4 md:mb-6 leading-tight">
                Transformă-ți Viața prin Nutriție Inteligentă
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-[#4a5568] mb-6 md:mb-8 leading-relaxed">
                Platforma digitală pentru rețete sănătoase și sustenabile, cu recomandări
                personalizate bazate pe AI, monitorizare sănătate și acces la producători locali.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button
                  onClick={() => navigate('/auth')}
                  data-testid="hero-cta-button"
                  size="lg"
                  className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full sm:w-auto"
                >
                  Începe Gratuit
                </Button>
                <Button
                  onClick={() => navigate('/education')}
                  variant="outline"
                  size="lg"
                  data-testid="learn-more-button"
                  className="border-2 border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-[#1A4D2E]/5 rounded-full text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full sm:w-auto"
                >
                  Află Mai Mult
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative rounded-2xl md:rounded-3xl overflow-hidden card-shadow aspect-[4/3] md:aspect-auto md:h-[400px] lg:h-[500px]">
                <img
                  src="https://images.pexels.com/photos/4397729/pexels-photo-4397729.jpeg"
                  alt="Healthy food plate"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E]/20 to-transparent" />
              </div>
              {/* Decorative blob */}
              <div className="absolute -z-10 -top-6 -right-6 md:-top-10 md:-right-10 w-48 h-48 md:w-64 md:h-64 bg-[#9BCF53]/20 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-3 md:mb-4">
              Funcționalități Puternice
            </h2>
            <p className="text-base md:text-lg text-[#4a5568] max-w-2xl mx-auto px-4">
              Tot ce ai nevoie pentru o viață sănătoasă într-o singură platformă
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 hover:shadow-md transition-all duration-300"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1A4D2E] rounded-full flex items-center justify-center mb-4 md:mb-6">
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                  </div>
                  <h3 className="font-serif text-lg md:text-xl text-[#1A4D2E] mb-2 md:mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-[#4a5568] leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-3 md:mb-4">
              Planuri de Abonament
            </h2>
            <p className="text-base md:text-lg text-[#4a5568] max-w-2xl mx-auto px-4">
              De la simplu la extraordinar - găsește ce ți se potrivește
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mt-8">
            {[
              {
                name: 'Free',
                price: '0',
                icon: '🌿',
                color: 'gray',
                features: ['1 plan de mese pe lună', 'Monitorizare de bază', 'Articole educaționale', 'Rețete simple'],
              },
              {
                name: 'Basic',
                price: '29.99',
                icon: '🌱',
                color: 'emerald',
                features: ['4 planuri de mese pe lună', 'Liste cumpărături', 'Recomandări AI', 'Fără reclame'],
              },
              {
                name: 'Premium',
                price: '59.99',
                icon: '⭐',
                color: 'purple',
                popular: true,
                features: ['Planuri NELIMITATE', 'Acces nutriționiști', 'Analiză sănătate', 'Rețete chefi'],
              },
              {
                name: 'Gold',
                price: '99.99',
                icon: '👑',
                color: 'amber',
                exclusive: true,
                features: ['TOTUL din Premium +', 'Consultații 1-la-1', 'Asistent 24/7', 'Evenimente VIP'],
              },
            ].map((plan, index) => {
              const colorClasses = {
                gray: { bg: 'bg-gray-50', border: 'border-gray-200', gradient: 'from-gray-400 to-gray-600', badge: 'bg-gray-500' },
                emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', gradient: 'from-emerald-400 to-emerald-600', badge: 'bg-emerald-500' },
                purple: { bg: 'bg-purple-50', border: 'border-purple-200', gradient: 'from-purple-500 to-purple-700', badge: 'bg-purple-500' },
                amber: { bg: 'bg-amber-50', border: 'border-amber-200', gradient: 'from-amber-400 to-yellow-500', badge: 'bg-amber-500' },
              };
              const colors = colorClasses[plan.color];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`${colors.bg} border-2 ${colors.border} rounded-2xl overflow-hidden card-shadow hover:shadow-xl transition-all duration-300 flex flex-col`}
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                >
                  {/* Header gradient bar */}
                  <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                  
                  {/* Badge area - fixed height */}
                  <div className="h-8 flex items-center justify-center">
                    {plan.popular && (
                      <div className="bg-purple-500 text-white text-xs font-medium px-3 py-1 rounded-full shadow-md">
                        ⭐ Cel Mai Popular
                      </div>
                    )}
                    {plan.exclusive && (
                      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-medium px-3 py-1 rounded-full shadow-md">
                        👑 Exclusiv
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 pt-2 flex flex-col flex-grow">
                    {/* Icon */}
                    <div className="text-4xl mb-3 text-center">{plan.icon}</div>
                    
                    {/* Name */}
                    <h3 className="font-serif text-2xl text-[#1A4D2E] mb-2 text-center">{plan.name}</h3>
                    
                    {/* Price */}
                    <div className="mb-6 text-center">
                      {plan.price === '0' ? (
                        <span className="text-3xl font-bold text-[#1A4D2E]">Gratuit</span>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-[#1A4D2E]">{plan.price}</span>
                          <span className="text-[#4a5568] ml-1 text-sm">RON/lună</span>
                        </>
                      )}
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-3 mb-6 flex-grow">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start text-[#4a5568] text-sm">
                          <div className={`w-5 h-5 ${colors.badge} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Button */}
                    <Button
                      onClick={() => navigate('/auth')}
                      data-testid={`select-plan-${plan.name.toLowerCase()}`}
                      className={`w-full rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all py-5 ${
                        plan.exclusive 
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600' 
                          : plan.popular 
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : plan.color === 'emerald'
                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                              : 'bg-[#1A4D2E] text-white hover:bg-[#143d24]'
                      }`}
                    >
                      {plan.price === '0' ? 'Începe Gratuit' : `Alege ${plan.name}`}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A4D2E] text-white py-12 px-6 md:px-12">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Heart className="w-6 h-6" />
            <span className="text-xl font-serif font-bold">NutriHealth</span>
          </div>
          <p className="text-white/80">
            © 2025 NutriHealth. Toate drepturile rezervate. | Platforma digitală pentru sănătate și nutriție
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;